import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Search, MapPin, Plus, X, Package, Clock, Navigation } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const LostFoundList = ({ onPost }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [activeTab, setActiveTab] = useState(''); // '' = all, 'lost', 'found'
  const [nearestMe, setNearestMe] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/lost-found/categories`)
      .then(res => setCategories(res.data.categories))
      .catch(() => {});
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: 50 };
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory) params.category = selectedCategory;
      if (activeTab) params.itemType = activeTab;
      if (nearestMe && userLocation) {
        params.lat = userLocation.lat;
        params.lng = userLocation.lng;
      }
      const res = await axios.get(`${API}/api/lost-found`, { params });
      setItems(res.data.items);
    } catch (err) {
      console.error('Error fetching lost & found:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory, activeTab, nearestMe, userLocation]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleSearch = () => fetchItems();

  const toggleNearestMe = () => {
    if (nearestMe) {
      setNearestMe(false);
      setLocationError('');
      return;
    }
    setLocationError('');
    if (userLocation) {
      setNearestMe(true);
      return;
    }
    if (!navigator.geolocation) {
      setLocationError(t('nearestMe.unavailable', 'Location is not available on this device'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setNearestMe(true);
      },
      () => {
        setLocationError(t('nearestMe.denied', 'Please enable location to use Nearest Me'));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const categoryIcons = {
    'Pets': '🐾', 'Keys': '🔑', 'Wallets/Bags': '👜',
    'Documents/ID': '📄', 'Phones/Electronics': '📱', 'Other': '📦'
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const created = new Date(dateStr);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d`;
    return created.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <>
      {/* Search */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              placeholder={t('lostFound.searchPlaceholder', 'Search lost & found items...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              data-testid="lf-search-input"
            />
            <button onClick={handleSearch} className="px-3 py-2 bg-blue-600 text-white rounded-lg" data-testid="lf-search-btn">
              <Search className="h-4 w-4" />
            </button>
            <button
              onClick={toggleNearestMe}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                nearestMe ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid="lf-nearest-me-btn"
            >
              <Navigation className={`h-4 w-4 ${nearestMe ? 'fill-current' : ''}`} />
              <span className="hidden sm:inline">{t('nearestMe.label', 'Nearest Me')}</span>
            </button>
          </div>
          {locationError && (
            <p className="mb-2 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-lg" data-testid="lf-location-error">
              {locationError}
            </p>
          )}

          {/* Lost / Found tabs */}
          <div className="flex gap-2 mb-3">
            {[
              { key: '', label: t('all', 'All') },
              { key: 'lost', label: t('lostFound.lost', 'Lost') },
              { key: 'found', label: t('lostFound.found', 'Found') },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? tab.key === 'lost' ? 'bg-red-500 text-white' : tab.key === 'found' ? 'bg-blue-500 text-white' : 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`lf-tab-${tab.key || 'all'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Category pills */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => setSelectedCategory('')}
              className={`px-3 py-1.5 rounded-full whitespace-nowrap text-xs font-medium transition-colors ${
                selectedCategory === '' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid="lf-cat-all"
            >
              {t('all', 'All')}
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full whitespace-nowrap text-xs font-medium transition-colors flex items-center gap-1 ${
                  selectedCategory === cat ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`lf-cat-${cat.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
              >
                <span>{categoryIcons[cat] || '📦'}</span>
                {t(`lostFound.cat.${cat}`, cat)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        {loading ? (
          <div className="animate-pulse grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map(i => <div key={i} className="bg-gray-200 h-48 rounded-xl"></div>)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-100">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg mb-2">{t('lostFound.noItems', 'No items found')}</p>
              <p className="text-gray-400 text-sm mb-6">
                {t('lostFound.beFirst', 'Post a lost or found item to help your community.')}
              </p>
              <button onClick={onPost}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                data-testid="lf-post-first-btn">
                {t('lostFound.postItem', 'Post Lost or Found Item')}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(`/lost-found/${item.id}`)}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden group border border-gray-100"
                data-testid={`lf-card-${item.id}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-blue-50 to-slate-50">
                  {item.photos && item.photos.length > 0 ? (
                    <img src={item.photos[0]} alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      style={{ objectPosition: 'center center' }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-4xl">{categoryIcons[item.category] || '📦'}</span>
                    </div>
                  )}
                  {/* Type badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                      item.itemType === 'lost' ? 'bg-red-500 text-white' : 'bg-blue-500 text-white'
                    }`}>
                      {item.itemType === 'lost' ? t('lostFound.lost', 'Lost') : t('lostFound.found', 'Found')}
                    </span>
                  </div>
                </div>

                <div className="p-3.5">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{item.title}</h3>
                    <span className="text-xs text-gray-400 whitespace-nowrap mt-0.5">{timeAgo(item.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                      {categoryIcons[item.category] || '📦'} {t(`lostFound.cat.${item.category}`, item.category)}
                    </span>
                    {item.distance != null && (
                      <span className="inline-flex items-center gap-0.5 text-blue-600 font-medium" data-testid={`lf-distance-${item.id}`}>
                        <Navigation className="h-3 w-3" />
                        {item.distance} km
                      </span>
                    )}
                    {item.location && !item.distance && (
                      <span className="inline-flex items-center gap-0.5 truncate">
                        <MapPin className="h-3 w-3 flex-shrink-0" /> {item.location}
                      </span>
                    )}
                  </div>
                  {item.date && (
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1.5">
                      <Clock className="h-3 w-3" />
                      {item.itemType === 'lost' ? t('lostFound.lostOn', 'Lost') : t('lostFound.foundOn', 'Found')}: {item.date}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default LostFoundList;
